'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import ICastLogo from '@/components/ICastLogo';
import { Mail, Lock, Eye, EyeOff, User, Radio, AlertCircle, Loader2, Check } from 'lucide-react';

const TIER_FEATURES: Record<string, string[]> = {
  basic: ['720p streaming', '1 platform', '5 AI generations/month', 'Basic analytics'],
  pro: ['1080p streaming', '5 platforms', '500 AI generations/month', 'Advanced analytics', 'Monetization'],
  enterprise: ['4K streaming', 'Unlimited platforms', 'Unlimited AI', 'Custom branding', 'Priority support'],
};

export default function RegisterPage() {
  const router = useRouter();
  const { signUp } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedTier, setSelectedTier] = useState<'basic' | 'pro' | 'enterprise'>('basic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      await signUp(email, password, { fullName });
      router.push('/member-dashboard');
      router.refresh();
    } catch (err: any) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <ICastLogo size="md" />
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-zinc-100">Create your account</h1>
            <p className="text-sm text-zinc-500 mt-1">Start broadcasting with I-Cast today</p>
          </div>

          {error && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertCircle size={15} className="text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-400">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full Name */}
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1.5">Full name</label>
              <div className="relative">
                <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  placeholder="Marcus Rivera"
                  className="w-full pl-9 pr-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-red-500/60 focus:ring-1 focus:ring-red-500/30 transition-all"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1.5">Email address</label>
              <div className="relative">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="w-full pl-9 pr-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-red-500/60 focus:ring-1 focus:ring-red-500/30 transition-all"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1.5">Password</label>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Min. 6 characters"
                  className="w-full pl-9 pr-10 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-red-500/60 focus:ring-1 focus:ring-red-500/30 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {/* Subscription Tier Selector */}
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-2">Choose your plan</label>
              <div className="grid grid-cols-3 gap-2">
                {(['basic', 'pro', 'enterprise'] as const).map((tier) => (
                  <button
                    key={tier}
                    type="button"
                    onClick={() => setSelectedTier(tier)}
                    className={`relative p-3 rounded-lg border text-left transition-all duration-150 ${
                      selectedTier === tier
                        ? tier === 'pro' ?'border-red-500/60 bg-red-500/10'
                          : tier === 'enterprise' ?'border-amber-500/60 bg-amber-500/10' :'border-zinc-500/60 bg-zinc-800' :'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                    }`}
                  >
                    <p className={`text-xs font-semibold capitalize mb-1 ${
                      selectedTier === tier
                        ? tier === 'pro' ? 'text-red-400' : tier === 'enterprise' ? 'text-amber-400' : 'text-zinc-200' :'text-zinc-400'
                    }`}>
                      {tier}
                    </p>
                    <div className="space-y-0.5">
                      {TIER_FEATURES[tier]?.slice(0, 2).map((f) => (
                        <p key={f} className="text-[10px] text-zinc-500 leading-tight">{f}</p>
                      ))}
                    </div>
                    {selectedTier === tier && (
                      <div className="absolute top-2 right-2">
                        <Check size={12} className={tier === 'pro' ? 'text-red-400' : tier === 'enterprise' ? 'text-amber-400' : 'text-zinc-300'} />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-red-600 hover:bg-red-500 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-all duration-150 active:scale-[0.98]"
            >
              {loading ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Creating account…
                </>
              ) : (
                <>
                  <Radio size={15} />
                  Create Account
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-zinc-800 text-center">
            <p className="text-sm text-zinc-500">
              Already have an account?{' '}
              <Link href="/login" className="text-red-400 hover:text-red-300 font-medium transition-colors">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-zinc-600 mt-6">
          © 2025 I-Cast. Professional Broadcasting Platform.
        </p>
      </div>
    </div>
  );
}
